import { useState, useEffect } from 'react';
import { useAuth } from "../../../contexts/AuthContext";
import { db } from "../../../firebase";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate, useParams } from "react-router";
import * as theme from '../../../theme';
import Heading from '../../../components/Heading';
import TruCard from '../../../components/TruCard';
import dayjs from 'dayjs';
import { Button } from '@mui/material';

export default function ViewClientForm() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { clientId, scanId } = useParams();
  const [clientForm, setClientForm] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (clientId && scanId) {
      fetchData();
    }
  }, [clientId, scanId]);

  async function fetchData() {
    try {
      setLoading(true);
      await Promise.all([fetchClientForm(), fetchClient()]);
    } finally {
      setLoading(false);
    }
  }

  async function fetchClientForm() {
    try {
      const scanRef = doc(db, 'clients', clientId, 'scans', scanId);
      const scanSnap = await getDoc(scanRef);
      if (scanSnap.exists() && scanSnap.data().clientForm) {
        setClientForm(scanSnap.data().clientForm);
      } else {
        setError('Client form data not found.');
      }
    } catch (err) {
      console.error('Failed to fetch client form:', err.message);
      setError('Failed to load client form: ' + err.message);
    }
  }

  async function fetchClient() {
    try {
      const clientRef = doc(db, 'clients', clientId);
      const clientSnap = await getDoc(clientRef);
      if (clientSnap.exists()) {
        setClient(clientSnap.data());
      } else {
        setError('Client data not found.');
      }
    } catch (err) {
      console.error('Failed to fetch client:', err.message);
      setError('Failed to load client: ' + err.message);
    }
  }

  function navigateBack() {
    navigate(`/clients/${clientId}/scans/${scanId}`);
  }

  if (loading) {
    return (
      <div style={theme.Container.basic}>
        <div style={theme.Heading}>Client Pre-Scan Form</div>
        <div>Loading...</div>
      </div>
    );
  }

  if (error || !clientForm) {
    return (
      <div style={theme.Container.basic}>
        <div style={theme.Heading}>Client Pre-Scan Form</div>
        <div style={theme.Error}>{error || 'No client form data available.'}</div>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => navigateBack()}
          style={{ marginTop: 20 }}
        >
          Back to Scan
        </Button>
      </div>
    );
  }

  const address = clientForm.street && clientForm.city && clientForm.state && clientForm.zip
    ? [
        clientForm.street,
        clientForm.address2 || null,
        clientForm.city,
        `${clientForm.state} ${clientForm.zip}`
      ].filter(Boolean).join(', ')
    : 'Incomplete address provided';

  return (
    <div style={{ ...theme.Container.flexColumnContainer, ...theme.Container.center800 }}>
      <div style={theme.Container.flexColumn}>
        <Heading
          title={client && client.firstName && client.lastName ? `${client.firstName} ${client.lastName}` : 'Client Information'}
          onClick={() => navigateBack()}
        />
      </div>
      <div style={theme.Container.fullMax}>
        <div style={theme.Container.flexOne}>
          <Heading
            title="Pre-Scan Form"
            subHeading={true}
          />
          <div style={{ ...theme.Container.flexColumn, flex: 2, gap: 20 }}>
            <TruCard title="Personal Information" style={{ width: '100%' }}>
              <div style={theme.TruCard.SubTextLeft}>
                <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Sex:</div>
                <div style={{ marginBottom: 20 }}>{clientForm.sex === 'M' ? 'Male' : clientForm.sex === 'F' ? 'Female' : 'N/A'}</div>
              </div>
            </TruCard>
            <TruCard title="Address" style={{ width: '100%' }}>
              <div style={theme.TruCard.SubTextLeft}>
                <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Address:</div>
                <div style={{ marginBottom: 20 }}>{address}</div>
              </div>
            </TruCard>
            {clientForm.sex === 'F' && (
              <TruCard title="Female-Specific Information" style={{ width: '100%' }}>
                <div style={theme.TruCard.SubTextLeft}>
                  <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Pregnant:</div>
                  <div style={{ marginBottom: 20 }}>{clientForm.female.isPregnant ? 'Yes' : 'No'}</div>
                  <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Trying to Get Pregnant:</div>
                  <div style={{ marginBottom: 20 }}>{clientForm.female.isTrying ? 'Yes' : 'No'}</div>
                  <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Nursing:</div>
                  <div style={{ marginBottom: 20 }}>{clientForm.female.isNursing ? 'Yes' : 'No'}</div>
                  {clientForm.female.isNursing && (
                    <>
                      <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Baby's Age:</div>
                      <div style={{ marginBottom: 20 }}>{clientForm.female.ageOfBaby || 'N/A'}</div>
                    </>
                  )}
                </div>
              </TruCard>
            )}
            <TruCard title="Group Detox Program" style={{ width: '100%' }}>
              <div style={theme.TruCard.SubTextLeft}>
                <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Participating:</div>
                <div style={{ marginBottom: 20 }}>{clientForm.isGroupDetox ? 'Yes' : 'No'}</div>
                {clientForm.isGroupDetox && (
                  <>
                    <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Program:</div>
                    <div style={{ marginBottom: 20 }}>{clientForm.groupDetox || 'N/A'}</div>
                  </>
                )}
              </div>
            </TruCard>
            <TruCard title="Concerns/Goals" style={{ width: '100%' }}>
              <div style={theme.TruCard.SubTextLeft}>
                <div style={{ fontWeight: 600, color: theme.Colors.Purple }}>Concerns or Goals:</div>
                <div style={{ marginBottom: 20 }}>{clientForm.concerns || 'None provided'}</div>
              </div>
            </TruCard>
          </div>
        </div>
      </div>
      <div style={{ marginTop: 20, padding: 20, textAlign: 'center', fontSize: `0.85em`, color: theme.Colors.GrayMedium }}>
        <div style={{ marginBottom: 6 }}>
          <span style={{ color: theme.Colors.Purple, fontWeight: 600 }}>Completed:</span> {clientForm.dateCreated ? dayjs(clientForm.dateCreated.toDate()).format('MMMM D, YYYY h:mm A') : 'N/A'}
        </div>
        <span style={{ color: theme.Colors.Purple, fontWeight: 600 }}>Modified:</span> {clientForm.dateModified ? dayjs(clientForm.dateModified.toDate()).format('MMMM D, YYYY h:mm A') : 'N/A'}
      </div>
    </div>
  );
}